"use client";

import React, { Suspense } from "react";
import {
  QueryClientProvider,
  QueryClient,
  QueryCache,
  MutationCache,
} from "@tanstack/react-query";
import axios from "axios";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "@mui/material";
import { FeedBackProvider } from "../providers/FeedBackProvider/FeedBackProvider";
import { ClientsFiltersProvider } from "@/providers/ClientsFiltersContext";
import theme from "./theme";
import { EarningsFiltersProvider } from "@/providers/EarningsFiltersContext";
import { ChatMessagesProvider } from "@/providers/ChatContext";

// Función global para manejar errores de autenticación
const handleGlobalAuthError = () => {
  // Limpiar storage
  localStorage.clear();
  sessionStorage.clear();

  // Forzar recarga para ir al login
  window.location.href = "/";
};

function Providers({ children }: React.PropsWithChildren) {
  const [client] = React.useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({
          onError: (error) => {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
              handleGlobalAuthError();
            }
          },
        }),
        mutationCache: new MutationCache({
          onError: (error) => {
            if (axios.isAxiosError(error) && error.response?.status === 401) {
              handleGlobalAuthError();
            }
          },
        }),
        defaultOptions: {
          queries: {
            retry: (failureCount, error) => {
              // No reintentar en errores 401
              if (axios.isAxiosError(error) && error.response?.status === 401) {
                return false;
              }
              return failureCount < 2;
            },
            refetchOnWindowFocus: false, // Evitar refetch automático que pueda causar 401s
            staleTime: 5 * 60 * 1000, // 5 minutos
          },
          mutations: {
            retry: (failureCount, error) => {
              if (axios.isAxiosError(error) && error.response?.status === 401) {
                return false;
              }
              return failureCount < 1;
            },
          },
        },
      })
  );

  // Exponer queryClient globalmente para el interceptor de Axios
  React.useEffect(() => {
    window.queryClient = client;
    return () => {
      delete window.queryClient;
    };
  }, [client]);

  // adding variables to adjust the chat colors in the css
  React.useEffect(() => {
    document.documentElement.style.setProperty(
      "--outgoing-message-bg",
      theme.palette.surface.light2 || "#E7F3FF"
    );
    document.documentElement.style.setProperty(
      "--incoming-message-bg",
      theme.palette.surface.main || "#B5E0FF"
    );
    document.documentElement.style.setProperty(
      "--send-message-button-bg",
      theme.palette.primary.main || "#355DE8"
    );
  }, [theme]);

  return (
    <QueryClientProvider client={client}>
      <ThemeProvider theme={theme}>
        <ClientsFiltersProvider>
          <EarningsFiltersProvider>
            <FeedBackProvider>
              <ChatMessagesProvider>
                <Suspense fallback={<div>Loading...</div>}>{children}</Suspense>
              </ChatMessagesProvider>
            </FeedBackProvider>
          </EarningsFiltersProvider>
        </ClientsFiltersProvider>
      </ThemeProvider>
      <ReactQueryDevtools buttonPosition="bottom-left" initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default Providers;
